/**
 * Authentication Service
 * 
 * Provides authentication operations using Supabase Auth with magic link authentication.
 * Includes email domain validation for beta access control.
 */

import { supabase } from './supabaseClient';
import type { User, Session } from '@supabase/supabase-js';
import type { UserProfile } from '../types';
import { getIPAddress, getGeoData, getUserAgent } from './geoService';

/**
 * Email domain validation result
 */
export interface EmailValidationResult {
  valid: boolean;
  errorKey?: string;
  errorParams?: Record<string, string>;
  suggestionKey?: string;
  suggestionParams?: Record<string, string>;
}

/**
 * Allowed email domains for beta access
 */
const ALLOWED_DOMAINS = ['gmail.com', 'outlook.com', 'qq.com'] as const;

/**
 * Common email typos and their corrections
 */
const EMAIL_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.co.uk': 'gmail.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.co.uk': 'outlook.com',
  'qq.co': 'qq.com',
  'qq.co.uk': 'qq.com',
};

/**
 * Validates email domain against allowed list
 * @param email - Email address to validate
 * @returns Validation result with error message and typo suggestions if applicable
 */
export function validateEmailDomain(email: string): EmailValidationResult {
  // Check for exactly one @ sign
  const atCount = (email.match(/@/g) || []).length;
  if (atCount !== 1) {
    return {
      valid: false,
      errorKey: 'emailGate.invalidEmailFormat',
    };
  }

  // Basic email format validation (RFC 5322 simplified)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      valid: false,
      errorKey: 'emailGate.invalidEmailFormat',
    };
  }

  // Extract domain (safe now since we know there's exactly one @)
  const domain = email.split('@')[1].toLowerCase();
  if (!domain) {
    return {
      valid: false,
      errorKey: 'emailGate.invalidEmailFormat',
    };
  }

  // Check if domain is in allowed list
  if (ALLOWED_DOMAINS.includes(domain as typeof ALLOWED_DOMAINS[number])) {
    return { valid: true };
  }

  // Check for common typos
  if (domain in EMAIL_TYPOS) {
    const correctDomain = EMAIL_TYPOS[domain];
    return {
      valid: false,
      errorKey: 'emailGate.domainNotAllowed',
      suggestionKey: 'emailGate.typoSuggestion',
      suggestionParams: { domain: correctDomain },
    };
  }

  return {
    valid: false,
    errorKey: 'emailGate.domainNotAllowed',
  };
}

/**
 * Signs in user with magic link (email OTP)
 * @param email - Email address
 * @param captchaToken - Optional Cloudflare Turnstile captcha token
 * @throws Error if validation fails or API call fails
 */
export async function signInWithMagicLink(email: string, captchaToken?: string): Promise<void> {
  // Sanitize email: trim whitespace and convert to lowercase
  const sanitizedEmail = email.trim().toLowerCase();
  
  if (!sanitizedEmail) {
    throw new Error('TRANSLATE:emailGate.invalidEmailFormat');
  }
  
  // Validate email domain
  const validation = validateEmailDomain(sanitizedEmail);
  if (!validation.valid) {
    // Build error message with translation keys
    let errorMessage = `TRANSLATE:${validation.errorKey}`;
    if (validation.suggestionKey && validation.suggestionParams) {
      // Include suggestion with parameters
      const suggestionParam = validation.suggestionParams.domain || '';
      errorMessage += `:SUGGESTION:${validation.suggestionKey}:${suggestionParam}`;
    }
    throw new Error(errorMessage);
  }

  // Send magic link via Supabase Auth
  // Get the current origin for redirect URL (supports localhost, Vercel, and any deployment)
  // This ensures redirect URLs work in both development and production
  // Use root path '/' to ensure clean redirect (Supabase will append auth tokens)
  const redirectTo = typeof window !== 'undefined' 
    ? `${window.location.origin}/`
    : '/';
  
  const { error } = await supabase.auth.signInWithOtp({
    email: sanitizedEmail,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: redirectTo,
      captchaToken,
    },
  });

  if (error) {
    // Map Supabase errors to translatable keys
    if (error.message.includes('rate limit') || error.message.includes('too many')) {
      throw new Error('TRANSLATE:emailGate.rateLimitExceeded');
    }
    if (error.message.includes('invalid')) {
      throw new Error('TRANSLATE:emailGate.invalidEmailFormat');
    }
    throw new Error(`TRANSLATE:emailGate.magicLinkFailed:${error.message}`);
  }
}

/**
 * Gets the current session
 * @returns Current session or null if not authenticated
 */
export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Gets the current user
 * @returns Current user or null if not authenticated
 */
export async function getUser(): Promise<User | null> {
  const { data } = await supabase.auth.getUser();
  return data.user;
}

/**
 * Signs out the current user
 */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw new Error(`Failed to sign out: ${error.message}`);
  }
}

/**
 * Gets user profile from database
 * @param userId - User ID (UUID)
 * @returns User profile or null if not found
 * @throws Error if database query fails
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    throw new Error(`Failed to fetch user profile: ${error.message}`);
  }

  return data as UserProfile;
}

/**
 * Updates user profile with IP address and geolocation data
 * Called after authentication to ensure user tracking data is captured
 * Non-blocking, fire-and-forget operation
 * 
 * @param userId - User ID to update
 */
export async function updateUserProfileWithGeoData(userId: string): Promise<void> {
  // Fire-and-forget analytics, don't block UI
  try {
    // Check if profile already has IP address (avoid unnecessary updates)
    const { data: existingProfile } = await supabase
      .from('user_profiles')
      .select('ip_address')
      .eq('id', userId)
      .single();

    // If profile already has IP address, skip update (already captured)
    if (existingProfile?.ip_address) {
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.log('[updateUserProfileWithGeoData] Profile already has IP address, skipping update');
      }
      return;
    }

    // Get IP address first (critical - always capture this)
    const ipAddressPromise = getIPAddress().catch((error) => {
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.warn('[updateUserProfileWithGeoData] IP address lookup failed:', error);
      }
      return null;
    });
    
    const ipAddress = await Promise.race([
      ipAddressPromise,
      new Promise<string | null>((resolve) =>
        setTimeout(() => {
          if (import.meta.env.VITE_DEBUG === 'true') {
            console.warn('[updateUserProfileWithGeoData] IP address lookup timed out after 2 seconds');
          }
          resolve(null);
        }, 2000)
      ),
    ]);

    // Get geolocation data (country, region, city) - non-blocking
    const geoInfoPromise = getGeoData().catch((error) => {
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.warn('[updateUserProfileWithGeoData] Geo lookup failed:', error);
      }
      return { country: null, region: null, city: null };
    });
    
    const geoInfo = await Promise.race([
      geoInfoPromise,
      new Promise<{ country: string | null; region: string | null; city: string | null }>((resolve) =>
        setTimeout(() => {
          if (import.meta.env.VITE_DEBUG === 'true') {
            console.warn('[updateUserProfileWithGeoData] Geo lookup timed out after 3 seconds');
          }
          resolve({ country: null, region: null, city: null });
        }, 3000)
      ),
    ]);

    // Update profile with IP and geo data
    // Always include all fields (even if null) so UPDATE can set them
    const updateData: Record<string, any> = {
      ip_address: ipAddress,
      country: geoInfo.country,
      region: geoInfo.region,
      city: geoInfo.city,
      user_agent: getUserAgent() || null,
    };

    const { error } = await supabase
      .from('user_profiles')
      .update(updateData)
      .eq('id', userId);

    if (error) {
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.error('[updateUserProfileWithGeoData] Failed to update profile:', error);
      }
    } else if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[updateUserProfileWithGeoData] Profile updated successfully:', {
        ip: ipAddress,
        country: geoInfo.country,
        region: geoInfo.region,
        city: geoInfo.city,
      });
    }
  } catch (error) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.error('[updateUserProfileWithGeoData] Unexpected error:', error);
    }
    // Silent fail - don't disrupt user experience
  }
}
